import { atom, useAtom } from "jotai";
import { useCallback, useEffect } from "react";
import { supabase } from "../lib/supabase";
import { todayInPeriodTimezone } from "../lib/dates";
import type {
  SearchParams,
  SortParams,
  FetchResult,
} from "../components/Manager/types";
import type { Database } from "../types/supabase";

export type Period = Database["public"]["Tables"]["periods"]["Row"];

// Atom for caching all periods
const periodsAtom = atom<Period[]>([]);

/**
 * Hook to fetch and cache all periods
 */
export function usePeriods() {
  const [periods, setPeriods] = useAtom(periodsAtom);

  const fetchAllPeriods = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from("periods")
        .select("*")
        .order("start_date", { ascending: false });

      if (error) throw error;
      setPeriods(data ?? []);
    } catch (err) {
      console.error("Error fetching periods:", err);
    }
  }, [setPeriods]);

  useEffect(() => {
    if (periods.length === 0) {
      fetchAllPeriods();
    }
  }, [fetchAllPeriods, periods.length]);

  return [periods, fetchAllPeriods] as const;
}

/**
 * Compute the status of a period based on start_date and end_date.
 * Comparison is done in Toronto timezone (see PERIOD_TIMEZONE).
 * Pure string comparison works because both sides are 'YYYY-MM-DD'.
 */
export function getPeriodStatus(period: Period): "current" | "past" | "future" {
  const today = todayInPeriodTimezone();
  if (today < period.start_date) return "future";
  if (today > period.end_date) return "past";
  return "current";
}

/**
 * Search periods by name prefix. Returns empty array for empty search string.
 * Used by autocomplete components.
 */
export async function searchPeriods(
  searchText: string,
  limit = 10,
): Promise<Period[]> {
  if (!searchText.trim()) {
    return [];
  }

  const { data, error } = await supabase
    .from("periods")
    .select("*")
    .ilike("name", `%${searchText}%`)
    .order("start_date", { ascending: false })
    .limit(limit);

  if (error) throw error;
  return data ?? [];
}

/**
 * Fetch a single period by ID.
 */
export async function fetchPeriodById(id: number): Promise<Period | null> {
  const { data, error } = await supabase
    .from("periods")
    .select("*")
    .eq("id", id)
    .single();

  if (error) {
    if (error.code === "PGRST116") return null; // Not found
    throw error;
  }
  return data;
}

/**
 * Check if a period has any marketing activities attached
 */
export async function hasPeriodMarketingActivities(
  periodId: number,
): Promise<boolean> {
  const { count, error } = await supabase
    .from("marketing_activities")
    .select("*", { count: "exact", head: true })
    .eq("period_id", periodId)
    .eq("is_deleted", false);

  if (error) throw error;
  return (count ?? 0) > 0;
}

export async function fetchPeriods(
  limit?: number,
  offset?: number,
  searchParams?: SearchParams,
  sortParams?: SortParams,
): Promise<FetchResult<Period>> {
  // First, get total count with search filters
  let countQuery = supabase
    .from("periods")
    .select("*", { count: "exact", head: true });

  // Apply text search filter for count (on name)
  if (searchParams?.text) {
    const searchText = `%${searchParams.text}%`;
    countQuery = countQuery.ilike("name", searchText);
  }

  // Apply status filter for count (using Toronto timezone)
  if (searchParams?.filters?.status) {
    const statusFilter = searchParams.filters.status as string;
    const today = todayInPeriodTimezone();

    if (statusFilter === "current") {
      countQuery = countQuery.lte("start_date", today).gte("end_date", today);
    } else if (statusFilter === "past") {
      countQuery = countQuery.lt("end_date", today);
    } else if (statusFilter === "future") {
      countQuery = countQuery.gt("start_date", today);
    }
  }

  const { count, error: countError } = await countQuery;
  if (countError) {
    console.error("Error fetching periods count:", countError);
    throw countError;
  }

  // Build the main query
  let query = supabase.from("periods").select("*");

  // Apply text search filter (on name)
  if (searchParams?.text) {
    const searchText = `%${searchParams.text}%`;
    query = query.ilike("name", searchText);
  }

  // Apply status filter (using Toronto timezone)
  if (searchParams?.filters?.status) {
    const statusFilter = searchParams.filters.status as string;
    const today = todayInPeriodTimezone();

    if (statusFilter === "current") {
      query = query.lte("start_date", today).gte("end_date", today);
    } else if (statusFilter === "past") {
      query = query.lt("end_date", today);
    } else if (statusFilter === "future") {
      query = query.gt("start_date", today);
    }
  }

  // Apply sorting (default: by start_date descending)
  if (sortParams) {
    query = query.order(sortParams.column, {
      ascending: sortParams.order === "asc",
    });
  } else {
    query = query.order("start_date", { ascending: true });
  }

  // Apply pagination
  if (limit !== undefined) {
    query = query.limit(limit);
  }
  if (offset !== undefined) {
    query = query.range(offset, offset + (limit ?? 10) - 1);
  }

  const { data, error } = await query;

  if (error) {
    console.error("Error fetching periods:", error);
    throw error;
  }

  const totalCount = count ?? 0;
  const hasMore =
    offset !== undefined && limit !== undefined
      ? offset + limit < totalCount
      : false;

  return {
    data: data ?? [],
    hasMore,
    totalCount,
  };
}
